import { useState, useRef, useEffect } from "react";
import { Box, Button, Chip } from "@mui/material";
import { Theme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import useResizeObserver from "use-resize-observer";
import CloseIcon from "@mui/icons-material/Close";
import { whiteSpaceToHyphen } from "utils";

const useStyles = makeStyles<Theme>((theme: Theme) => {
    return {
        chip: {
            margin: theme.spacing(0.5),
            fontWeight: 600,
            height: 28,
            borderWidth: "1px",
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.light,
            background: theme.palette.primary.light,
            "&:hover, &:focus": {
                background: `${theme.palette.primary.light} !important`,
                boxShadow: "0 2px 7px 0 #00000026",
            },
            "& .MuiChip-label": {
                maxWidth: 160,
                textOverflow: "ellipsis",
                whiteSpace: "pre",
                overflow: "hidden",
                fontSize: 14,
                fontWeight: 400,
                height: 24,
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(1),
                color: theme.palette.text.primary,
                letterSpacing: "0.4px",
            },
            "& .MuiChip-deleteIcon": {
                color: theme.palette.text.primary,
                width: 18,
                fontSize: 14,
                "&:hover": {
                    color: theme.palette.text.primary,
                },
            },
        },
        showLessBtn: {
            bottom: 0,
            right: 0,
            color: theme.palette.secondary.main,
            textDecoration: "underline",
            textUnderlineOffset: "4px",
            justifyContent: "end",
            "& .MuiButton-root": {
                padding: theme.spacing(1),
            },
            "&:hover": {
                textDecoration: "underline",
            },
        },
    };
});

const TeamChips = ({
    data,
    chips,
    onChipSelect,
    handleOpenTeamsModal,
    removeTeam,
}: {
    data: any;
    chips: any[];
    onChipSelect: (value: string) => void;
    handleOpenTeamsModal: (e: React.MouseEvent<HTMLDivElement, MouseEvent>, id: string) => void;
    removeTeam: (user_id: number, teamName: string) => Promise<void>;
}) => {
    const classes = useStyles();

    const [chipConfig, setChipConfig] = useState<any>(null);
    const [hiddenChipsCount, setHiddenChipsCount] = useState(0);
    const [showAllChips, setShowAllChips] = useState<boolean>(false);

    const chipRefs = useRef<HTMLDivElement[]>([]);
    const chipsWrapperRef = useRef<HTMLDivElement>(null);
    const { ref: resizableWrapper, width = -1, height = -1 } = useResizeObserver<HTMLDivElement>();

    useEffect(() => {
        chipRefs.current = chipRefs.current.filter((ref) => ref !== null);

        if (chipRefs.current[0]) {
            const config = chipRefs.current[0].getBoundingClientRect();
            setChipConfig(config);

            if (!showAllChips) {
                setHiddenChipsCount(
                    chipRefs.current.filter((chip) => chip && chip.getBoundingClientRect().y > config.y + config.height)
                        .length
                );
            } else {
                setHiddenChipsCount(0);
            }
        }
    }, [width, height, showAllChips, chips]);

    const handleChipClick = (event: React.MouseEvent<HTMLElement, MouseEvent>, chipName: string) => {
        event.stopPropagation();
        onChipSelect(chipName);
    };

    const toggleShowAllChips = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.stopPropagation();
        setShowAllChips(!showAllChips);
    };

    const handleRemoveTeam = async (chipName: string) => {
        try {
            await removeTeam(data.id, chipName);
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <Box
            sx={{
                ...(!showAllChips && { display: "flex" }),
                flexWrap: "nowrap",
                alignItems: "center",
                position: "relative",
            }}
            ref={resizableWrapper}
        >
            <Box
                sx={{
                    height: showAllChips ? "auto" : chipConfig?.height + 4,
                    overflow: showAllChips ? "visible" : "hidden",
                }}
                ref={chipsWrapperRef}
            >
                {chips.length ? (
                    chips.map((chip, i) => (
                        <Chip
                            key={whiteSpaceToHyphen(chip.name)}
                            onClick={(e) => handleChipClick(e, chip.name)}
                            className={classes.chip}
                            label={chip.name}
                            ref={(el) => {
                                chipRefs.current[i] = el as HTMLDivElement;
                            }}
                            onDelete={() => handleRemoveTeam(chip.name)}
                            deleteIcon={<CloseIcon />}
                        />
                    ))
                ) : (
                    <Chip label="no teams assigned" variant="outlined" sx={{ opacity: 0.5 }} />
                )}
                {!Boolean(hiddenChipsCount) && (
                    <Chip
                        key="add-team"
                        label="+"
                        variant="outlined"
                        sx={{ marginLeft: "8px", height: 28 }}
                        onClick={(ev) => handleOpenTeamsModal(ev, data.id)}
                    />
                )}
            </Box>

            {Boolean(hiddenChipsCount) && !showAllChips && (
                <Chip className={classes.chip} label={`+${hiddenChipsCount}`} onClick={toggleShowAllChips} />
            )}
            {Boolean(hiddenChipsCount) && !showAllChips && (
                <Chip
                    key="add-team"
                    label="+"
                    variant="outlined"
                    sx={{ marginLeft: "8px", height: 28 }}
                    onClick={(ev) => handleOpenTeamsModal(ev, data.id)}
                />
            )}

            {showAllChips && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                    }}
                >
                    <Button size="small" variant="text" className={classes.showLessBtn} onClick={toggleShowAllChips}>
                        Show Less
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default TeamChips;
